import { useState, useEffect, useCallback, useRef } from 'react';
import { queryService } from '../../services/queryService';
import { useSnackbar } from '../../components/common/Snackbar';

/**
 * Hook to manage query state and fetching in ViewQueryModal
 * @param {boolean} open - Whether modal is open
 * @param {string} queryId - Query ID to fetch
 * @param {Object} initialQuery - Initial query data (optional)
 * @param {function} onClose - Callback when modal closes
 * @param {function} onQueryFetched - Callback when query is fetched (for timeline)
 * @returns {Object} Query state and handlers
 */
export const useQueryModalState = ({ open, queryId, initialQuery, onClose, onQueryFetched, onQueryUpdated }) => {
  const { showSnackbar } = useSnackbar();
  const [query, setQuery] = useState(initialQuery || null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const hasInitializedRef = useRef(false);

  const fetchQuery = useCallback(async ({ silent = false } = {}) => {
    if (!queryId) return;

    if (!silent) setLoading(true);
    setError(null);

    try {
      const queryData = await queryService.getQueryById(queryId);
      setQuery(queryData);
      // Call callback to trigger timeline fetch
      if (onQueryFetched) {
        onQueryFetched();
      }
    } catch (err) {
      const message = err.response?.data?.message || 'Failed to load query details';
      setError(message);
      
      // Don't block viewing on access errors - allow everyone to view
      // If we have initial query data, use it even if fetch fails
      if (initialQuery && initialQuery._id === queryId) {
        setQuery(initialQuery);
        if (onQueryFetched) {
          onQueryFetched();
        }
        // console.warn('Using provided query data due to access restriction:', message);
      } else if (message.toLowerCase().includes('access') || message.toLowerCase().includes('permission')) {
        // Don't close modal or show error for access issues - allow viewing
        // console.warn('Access check warning (allowing view):', message);
        // Don't show error snackbar for access issues
      } else {
        showSnackbar(message, 'error');
        onClose();
      }
    } finally {
      if (!silent) setLoading(false);
    }
  }, [queryId, initialQuery, onClose, showSnackbar, onQueryFetched]);

  const updateQuery = useCallback((updatedQuery) => {
    setQuery(updatedQuery);
    if (onQueryUpdated) {
      onQueryUpdated(updatedQuery);
    }
  }, [onQueryUpdated]);

  // Fetch query when modal opens (only once per modal session)
  // This matches the backup behavior - only initialize once when modal opens
  useEffect(() => {
    if (open && queryId && !hasInitializedRef.current) {
      hasInitializedRef.current = true;
      // If we already have the query data, render it immediately but still refetch
      // so the modal never shows stale data after edits.
      if (initialQuery && initialQuery._id === queryId) {
        setQuery(initialQuery);
        fetchQuery({ silent: true });
        return;
      }
      fetchQuery();
    }
    if (!open) {
      // Reset flag when modal closes
      hasInitializedRef.current = false;
    }
  }, [open, queryId, fetchQuery, onQueryFetched]); // Removed initialQuery from deps to prevent re-runs

  // Reset query when modal closes
  useEffect(() => {
    if (!open) {
      setQuery(null);
      setError(null);
      hasInitializedRef.current = false;
    }
  }, [open]);

  return {
    query,
    loading,
    error,
    fetchQuery,
    updateQuery,
    setQuery,
  };
};
